// src/services/videoFavorites.js
// Firestore helpers for video (Shorts) favorites
// Collection: videoFavorites — doc ID: ${userId}_${videoId}
// Schema: { userId, videoId, createdAt }
// likeCount maintained on videoStats/${videoId} via atomic increment/decrement

import { db, firebaseEnabled } from './firebase';
import { doc, getDoc, collection, query, where, getDocs, increment } from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import { setDoc, deleteDoc, updateDoc } from './firestoreWrite';

// ── Set favorite state directly — no pre-read. ──
// See classFavorites.js for the rationale: caller passes the intended state so the
// Firestore write queues immediately, surviving a pull-to-refresh that lands during
// what would otherwise be a getDoc round-trip.
export async function setVideoFavoriteState(userId, videoId, shouldFavorite) {
    if (!firebaseEnabled || !db || !userId || !videoId) return shouldFavorite;
    try {
        const favId = `${userId}_${videoId}`;
        const favRef = doc(db, 'videoFavorites', favId);
        const statsRef = doc(db, 'videoStats', videoId);
        if (shouldFavorite) {
            await setDoc(favRef, { userId, videoId, createdAt: new Date().toISOString() });
            // setDoc with merge creates the stats doc if needed
            try { await setDoc(statsRef, { likeCount: increment(1) }, { merge: true }); } catch (e) { /* non-critical */ }
        } else {
            await deleteDoc(favRef);
            try { await updateDoc(statsRef, { likeCount: increment(-1) }); } catch (e) { /* stats doc may not exist yet */ }
        }
        return shouldFavorite;
    } catch (e) {
        console.error('Error setting video favorite state:', e);
        return !shouldFavorite;
    }
}

// ── Backward-compat wrapper ──
export async function toggleVideoFavorite(userId, videoId) {
    if (!firebaseEnabled || !db || !userId || !videoId) return false;
    try {
        const favId = `${userId}_${videoId}`;
        const favRef = doc(db, 'videoFavorites', favId);
        const existing = await getDoc(favRef);
        return await setVideoFavoriteState(userId, videoId, !existing.exists());
    } catch (e) {
        console.error('Error toggling video favorite:', e);
        return false;
    }
}

// ── Get all videoIds the user has favorited ──
export async function getUserVideoFavorites(userId) {
    if (!firebaseEnabled || !db || !userId) return new Set();
    try {
        const q = query(collection(db, 'videoFavorites'), where('userId', '==', userId));
        const snap = await getDocs(q);
        return new Set(snap.docs.map((d) => d.data().videoId));
    } catch (e) {
        console.error('Error fetching video favorites:', e);
        return new Set();
    }
}

// ── Get like counts for a list of video IDs ──
export async function getVideoLikeCounts(videoIds) {
    if (!firebaseEnabled || !db || !videoIds || videoIds.length === 0) return {};
    try {
        const counts = {};
        // Batch fetch — Firestore doesn't support IN queries on doc IDs,
        // so fetch individually (fast for small collections, cached by SDK)
        await Promise.all(videoIds.map(async (id) => {
            try {
                const snap = await getDoc(doc(db, 'videoStats', id));
                if (snap.exists()) counts[id] = snap.data().likeCount || 0;
            } catch (e) { /* individual fetch failure is non-critical */ }
        }));
        return counts;
    } catch (e) {
        console.error('Error fetching video like counts:', e);
        return {};
    }
}
